<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { Guest, GuestClass, AdditionalQuestion, QuestionType } from '@/types/guest';
import { useAdditionalQuestionsStore } from '@/stores/additionalQuestionsStore/additionalQuestionsStore';

const props = defineProps<{
  guest: Guest;
  mode?: 'edit' | 'add';
}>();

const emit = defineEmits<{
  (e: 'save', guest: Guest): void;
  (e: 'cancel'): void;
}>();

const additionalQuestionsStore = useAdditionalQuestionsStore();
const questionTemplates = computed(() => additionalQuestionsStore.getQuestionTemplates);

const editingGuest = ref(new GuestClass(props.guest));
const isLoading = ref(false);

type OldFormatQuestion = Record<string, string | number | boolean | (string | number | boolean)[] | undefined>;
type QuestionFormat = AdditionalQuestion | OldFormatQuestion;

const convertToNewFormat = (questions: QuestionFormat[]): AdditionalQuestion[] => {
  return questions.map((question) => {
    // If question is already in new format, return as is
    if ('id' in question && 'answer' in question) {
      return question as AdditionalQuestion;
    }

    // Convert old format to new format
    const entries = Object.entries(question);
    if (entries.length === 0) {
      return { id: '', type: QuestionType.TEXT, answer: undefined };
    }

    const firstEntry = entries[0];
    if (!firstEntry) {
      return { id: '', type: QuestionType.TEXT, answer: undefined };
    }

    const [id, answer] = firstEntry;
    return {
      id,
      type: QuestionType.TEXT,
      answer: answer as string | number | boolean | (string | number | boolean)[] | undefined,
    };
  });
};

onMounted(() => {
  // Convert any old format questions to new format
  editingGuest.value.additionalQuestions = convertToNewFormat(editingGuest.value.additionalQuestions);

  // Ensure all question templates are present with correct types
  const existingQuestions = new Map(editingGuest.value.additionalQuestions.map((q) => [q.id, q]));
  questionTemplates.value.forEach((template) => {
    if (!existingQuestions.has(template.id)) {
      editingGuest.value.additionalQuestions.push({
        id: template.id,
        type: template.type || QuestionType.TEXT,
        answer: template.type === QuestionType.ARRAY ? [] : template.type === QuestionType.BOOLEAN ? false : '',
      });
    } else {
      // Update type if it changed in template
      const existingQuestion = existingQuestions.get(template.id)!;
      if (existingQuestion.type !== template.type) {
        existingQuestion.type = template.type || QuestionType.TEXT;
        // Convert answer to match new type
        if (template.type === QuestionType.BOOLEAN) {
          existingQuestion.answer = Boolean(existingQuestion.answer);
        } else if (template.type === QuestionType.ARRAY) {
          existingQuestion.answer = Array.isArray(existingQuestion.answer) ? existingQuestion.answer : [];
        } else {
          existingQuestion.answer = String(existingQuestion.answer || '');
        }
      }
    }
  });
});

const getStatusIcon = (status: boolean | undefined) => {
  if (status === true) return 'pi pi-check-circle text-green-500';
  if (status === false) return 'pi pi-times-circle text-red-500';
  return 'pi pi-clock text-yellow-500';
};

const getStatusText = (status: boolean | undefined) => {
  if (status === true) return 'Potwierdzono';
  if (status === false) return 'Odmówiono';
  return 'Oczekuje';
};

const getStatusBooleanText = (status: boolean | undefined) => {
  if (status === true) return 'Tak';
  if (status === false) return 'Nie';
  return 'Brak odpowiedzi';
};

const onSave = async () => {
  isLoading.value = true;
  try {
    await emit('save', editingGuest.value);
  } finally {
    isLoading.value = false;
  }
};

const onCancel = () => {
  emit('cancel');
};

const addQuestion = () => {
  // Find first unused template
  const usedQuestionIds = new Set(editingGuest.value.additionalQuestions.map((q) => q.id));
  const unusedTemplate = questionTemplates.value.find((template) => !usedQuestionIds.has(template.id));

  if (unusedTemplate) {
    editingGuest.value.additionalQuestions.push({
      id: unusedTemplate.id,
      type: unusedTemplate.type || QuestionType.TEXT,
      answer:
        unusedTemplate.type === QuestionType.ARRAY ? [] : unusedTemplate.type === QuestionType.BOOLEAN ? false : '',
    });
  }
};
</script>

<template>
  <div class="guest-edit-popup">
    <div class="guest-edit-popup__content">
      <div class="guest-edit-popup__header">
        <h2>{{ mode === 'add' ? 'Dodaj nowego gościa' : 'Edytuj gościa' }}</h2>
      </div>
      <div class="guest-edit-popup__body">
        <div class="guest-edit-popup__form">
          <div class="guest-edit-popup__input-group">
            <label>Imię</label>
            <input
              v-model="editingGuest.name"
              type="text"
              class="guest-edit-popup__input"
              placeholder="Imię"
            />
          </div>
          <div class="guest-edit-popup__input-group">
            <label>Nazwisko</label>
            <input
              v-model="editingGuest.surname"
              type="text"
              class="guest-edit-popup__input"
              placeholder="Nazwisko"
            />
          </div>
          <div class="guest-edit-popup__input-group">
            <label>Status</label>
            <Dropdown
              v-model="editingGuest.presence"
              :options="[
                { label: 'Potwierdzono', value: true },
                { label: 'Odmówiono', value: false },
                { label: 'Oczekuje', value: undefined },
              ]"
              option-label="label"
              option-value="value"
              class="guest-edit-popup__status-dropdown"
              @change="(e) => (editingGuest.presence = e.value)"
            >
              <template #value="slotProps">
                <div class="guest-edit-popup__status-value">
                  <i :class="getStatusIcon(slotProps.value)"></i>
                  <span>{{ getStatusText(slotProps.value) }}</span>
                </div>
              </template>
              <template #option="slotProps">
                <div class="guest-edit-popup__status-option">
                  <i :class="getStatusIcon(slotProps.option.value)"></i>
                  <span>{{ slotProps.option.label }}</span>
                </div>
              </template>
            </Dropdown>
          </div>
          <div class="guest-edit-popup__questions">
            <h3>Pytania dodatkowe</h3>
            <div
              v-for="(question, index) in editingGuest.additionalQuestions"
              :key="index"
              class="guest-edit-popup__question"
            >
              <div class="guest-edit-popup__question-header">
                <span class="guest-edit-popup__question-label">{{ question.id }}</span>
              </div>

              <!-- Boolean type question -->
              <Dropdown
                v-if="question.type === QuestionType.BOOLEAN"
                v-model="question.answer"
                :options="[
                  { label: 'Tak', value: true },
                  { label: 'Nie', value: false },
                ]"
                option-label="label"
                option-value="value"
                class="guest-edit-popup__status-dropdown"
                @change="(e) => (question.answer = e.value)"
              >
                <template #value="slotProps">
                  <div class="guest-edit-popup__status-value">
                    <i :class="getStatusIcon(slotProps.value)"></i>
                    <span>{{ getStatusBooleanText(slotProps.value) }}</span>
                  </div>
                </template>
                <template #option="slotProps">
                  <div class="guest-edit-popup__status-option">
                    <i :class="getStatusIcon(slotProps.option.value)"></i>
                    <span>{{ slotProps.option.label }}</span>
                  </div>
                </template>
              </Dropdown>

              <!-- Array type question -->
              <div
                v-else-if="question.type === QuestionType.ARRAY"
                class="guest-edit-popup__array-inputs"
              >
                <div
                  v-for="(_, arrayIndex) in (question.answer as any[]) || []"
                  :key="arrayIndex"
                  class="guest-edit-popup__array-item"
                >
                  <input
                    v-model="(question.answer as any[])[arrayIndex]"
                    type="text"
                    class="guest-edit-popup__input"
                    placeholder="Wprowadź wartość"
                  />
                  <button
                    class="guest-edit-popup__remove-array-item"
                    @click="() => (question.answer as any[]).splice(arrayIndex, 1)"
                  >
                    <i class="pi pi-times"></i>
                  </button>
                </div>
                <button
                  class="guest-edit-popup__add-array-item"
                  @click="
                    () => {
                      if (!Array.isArray(question.answer)) question.answer = [];
                      (question.answer as any[]).push('');
                    }
                  "
                >
                  <i class="pi pi-plus"></i>
                  Dodaj element
                </button>
              </div>

              <!-- Text type question -->
              <input
                v-else
                v-model="question.answer"
                type="text"
                class="guest-edit-popup__input"
                placeholder="Wprowadź odpowiedź"
              />
            </div>
            <button
              v-if="editingGuest.additionalQuestions.length < questionTemplates.length"
              class="guest-edit-popup__add-button"
              @click="addQuestion"
            >
              <i class="pi pi-plus"></i>
              Dodaj pytanie
            </button>
          </div>
        </div>
      </div>
      <div class="guest-edit-popup__footer">
        <button
          class="guest-edit-popup__button guest-edit-popup__button--success"
          :disabled="isLoading"
          @click="onSave"
        >
          <i
            v-if="isLoading"
            class="pi pi-spin pi-spinner"
          ></i>
          <span v-else>Zapisz</span>
        </button>
        <button
          class="guest-edit-popup__button guest-edit-popup__button--danger"
          :disabled="isLoading"
          @click="onCancel"
        >
          Anuluj
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.guest-edit-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgb(0 0 0 / 50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  &__content {
    background-color: $white;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 12px rgb(0 0 0 / 15%);
  }

  &__header {
    padding: 1rem;
    border-bottom: 1px solid $brown-300;
    background-color: $brown-600;
    color: $black-300;

    h2 {
      margin: 0;
      font-size: 1.5rem;
    }
  }

  &__body {
    padding: 1.5rem;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__input-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid $brown-300;
    border-radius: 4px;
    background-color: $white;
    color: $black-300;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: $brown-200;
    }
  }

  &__status-dropdown {
    width: 100%;
  }

  &__status-value,
  &__status-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      font-size: 1.2rem;
    }
  }

  &__questions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__question {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  &__question-label {
    font-weight: 500;
  }

  &__question-type {
    font-size: 0.9em;
    color: $brown-300;
    padding: 0.2rem 0.5rem;
    background-color: $brown-700;
    border-radius: 4px;
    text-transform: capitalize;
  }

  &__add-button {
    padding: 0.5rem;
    background: none;
    border: 1px dashed $brown-300;
    border-radius: 4px;
    color: $brown-300;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: $brown-200;
      color: $brown-200;
    }
  }

  &__footer {
    padding: 1rem;
    border-top: 1px solid $brown-300;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  &__button {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    &--success {
      background-color: $brown-200;
      color: $white;

      &:hover {
        background-color: $brown-100;
      }
    }

    &--danger {
      background-color: $brown-700;
      color: $white;

      &:hover {
        background-color: $brown-100;
      }
    }
  }

  &__array-inputs {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__array-item {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  &__remove-array-item {
    padding: 0.5rem;
    background: none;
    border: none;
    color: $brown-300;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: $brown-100;
    }
  }

  &__add-array-item {
    padding: 0.5rem;
    background: none;
    border: 1px dashed $brown-300;
    border-radius: 4px;
    color: $brown-300;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
    margin-top: 0.5rem;

    &:hover {
      border-color: $brown-200;
      color: $brown-200;
    }
  }
}
</style>
